import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiPost, apiGet } from '../../lib/api-client';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../contexts/AuthContext';

interface SubSellerItem {
  id: string;
  name: string;
  level: number;
  payinSharePercent: number;
  payoutSharePercent: number;
}

export function SubSellerCreate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [payinSharePercent, setPayinSharePercent] = useState('');
  const [payoutSharePercent, setPayoutSharePercent] = useState('');
  const [parentId, setParentId] = useState('');
  const [error, setError] = useState('');

  // Get existing sub-sellers for parent selection
  const { data: subSellersData } = useQuery({
    queryKey: ['seller', 'sub-sellers'],
    queryFn: () =>
      apiGet<{ items: SubSellerItem[]; total: number }>('/seller/sub-sellers'),
  });

  const subSellers = subSellersData?.ok ? subSellersData.data.items : [];
  // Only level 1 and 2 can be parents (max depth 3)
  const possibleParents = subSellers.filter((ss) => ss.level < 3);

  const parentOptions = [
    { value: '', label: t('seller.directSubSeller') },
    ...possibleParents.map((ss) => ({
      value: ss.id,
      label: `${'  '.repeat(ss.level - 1)}${ss.name} (${ss.payinSharePercent}% / ${ss.payoutSharePercent}%)`,
    })),
  ];

  const createMutation = useMutation({
    mutationFn: () =>
      apiPost('/seller/sub-sellers', {
        parentId: parentId || user?.sellerId || '',
        name,
        username,
        password,
        payinSharePercent: parseFloat(payinSharePercent) || 0,
        payoutSharePercent: parseFloat(payoutSharePercent) || 0,
      }),
    onSuccess: (res) => {
      if (res.ok) {
        queryClient.invalidateQueries({ queryKey: ['seller', 'sub-sellers'] });
        navigate('/seller/sub-sellers');
      } else {
        setError(res.error.message);
      }
    },
    onError: (err) => {
      setError((err as Error).message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    createMutation.mutate();
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">{t('seller.createSubSeller')}</h1>

      <Card className="max-w-xl">
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4 pt-4">
            {error && (
              <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">{error}</div>
            )}

            <Input
              label={t('sellers.name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />

            <Input
              label={t('auth.username')}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />

            <Input
              label={t('auth.password')}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />

            <Input
              label={`${t('sellers.payinFee')} (%)`}
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={payinSharePercent}
              onChange={(e) => setPayinSharePercent(e.target.value)}
              required
            />

            <Input
              label={`${t('sellers.payoutFee')} (%)`}
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={payoutSharePercent}
              onChange={(e) => setPayoutSharePercent(e.target.value)}
              required
            />

            <Select
              label={t('seller.parentSeller')}
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
              options={parentOptions}
            />

            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? t('common.loading') : t('common.save')}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/seller/sub-sellers')}
              >
                {t('common.cancel')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
